import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, PenLine, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { createWriterDraft } from '@/lib/writer/createWriterDraft';

interface CommandBarProps {
  name?: string | null;
}

/** Greeting and the page's primary action. */
const CommandBar = ({ name }: CommandBarProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!user || creating) return;
    setCreating(true);
    const id = await createWriterDraft(user.id);
    setCreating(false);
    if (!id) {
      toast.error('Could not create draft');
      return;
    }
    navigate(`/write/${id}`);
  };

  return (
    <header className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between mb-12">
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
          <Sparkles className="h-3.5 w-3.5" />
          <span>Your workspace</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
          {name ? `Good to see you, ${name}` : 'Good to see you'}
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl">
          Write the paper. The reviewer reads along.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={handleCreate} disabled={creating}>
          {creating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <PenLine className="h-4 w-4 mr-2" />
          )}
          New draft
        </Button>
      </div>
    </header>
  );
};

export default CommandBar;
